import React from 'react';
import SEO from '../../components/SEO';

const CaseStudy2 = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      <SEO
        title="Agentic BIM Compliance Checker — Case Study | STRAY Apps"
        description="An intelligent Building Information Modeling (BIM) compliance checker that leverages AI to automatically validate 3D building models against multiple regulatory authorities."
        path="/case-studies/casestudy2"
      />
      <div className="container mx-auto px-4 py-16 max-w-4xl">
        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <i className="fi fi-sr-cubes text-6xl text-white"></i>
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">
                Agentic BIM Compliance Checker for IFC models
              </h1>
              <p className="text-purple-400 text-lg">AI-Powered BIM Compliance Solution</p>
            </div>
          </div>
        </div>

        {/* Overview */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-white mb-4">Overview</h2>
          <p className="text-gray-300 text-lg leading-relaxed">
            An intelligent Building Information Modeling (BIM) compliance checker that leverages AI
            to automatically validate 3D building models against multiple regulatory authorities.
            This solution streamlines the compliance process, reduces manual review time, and ensures
            building designs meet all necessary standards before construction begins.
          </p>
        </section>

        {/* Key Features */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-white mb-6">Key Features</h2>
          <div className="grid gap-4">
            <div className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 rounded-xl p-6 border border-purple-500/30 flex items-start gap-4">
              <span className="text-3xl">📤</span>
              <div>
                <h3 className="text-xl font-semibold text-purple-300 mb-2">
                  IFC Upload
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  Extract building data to semantic format, enabling comprehensive analysis and
                  validation of complex building information models.
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 rounded-xl p-6 border border-purple-500/30 flex items-start gap-4">
              <span className="text-3xl">✅</span>
              <div>
                <h3 className="text-xl font-semibold text-purple-300 mb-2">
                  Multi-Agency Compliance
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  Auto-validate against 7 Singapore authorities including BCA, SCDF, URA, and more,
                  ensuring comprehensive regulatory compliance.
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 rounded-xl p-6 border border-purple-500/30 flex items-start gap-4">
              <span className="text-3xl">🎨</span>
              <div>
                <h3 className="text-xl font-semibold text-purple-300 mb-2">
                  3D Viewer
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  Interactive model visualization with violation highlighting, allowing instant
                  identification and location of compliance issues.
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 rounded-xl p-6 border border-purple-500/30 flex items-start gap-4">
              <span className="text-3xl">🔍</span>
              <div>
                <h3 className="text-xl font-semibold text-purple-300 mb-2">
                  SPARQL Queries
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  Real-time rule validation and component search using powerful semantic query
                  language for precise compliance checking.
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 rounded-xl p-6 border border-purple-500/30 flex items-start gap-4">
              <span className="text-3xl">⏰</span>
              <div>
                <h3 className="text-xl font-semibold text-purple-300 mb-2">
                  Scheduled Checks
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  Automated compliance monitoring on model updates, ensuring continuous validation
                  throughout the design iteration process.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Demo Section - Video */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-white mb-6">Demo</h2>
          <div className="rounded-2xl overflow-hidden border-2 border-purple-500/30 bg-slate-800/50">
            <video
              controls
              className="w-full"
            >
              <source src="/videos/case-study-2/demo.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
          <p className="text-gray-400 text-sm mt-3 text-center">
            Watch the BIM compliance checker in action
          </p>
        </section>

        {/* Technical Stack */}
        <section className="mb-12 bg-slate-800/50 rounded-xl p-8 border border-purple-500/20">
          <h2 className="text-3xl font-bold text-white mb-6">Technical Implementation</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-xl font-semibold text-purple-300 mb-3">Core Technologies</h3>
              <ul className="text-gray-300 space-y-2">
                <li>• Gemini AI for intelligent compliance analysis</li>
                <li>• IFC (Industry Foundation Classes) parser</li>
                <li>• SPARQL query engine for semantic validation</li>
                <li>• WebGL-based 3D visualization</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-300 mb-3">Key Capabilities</h3>
              <ul className="text-gray-300 space-y-2">
                <li>• Multi-authority rule engine</li>
                <li>• Real-time violation detection</li>
                <li>• Automated scheduling system</li>
                <li>• Semantic data extraction</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Compliance Authorities */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-white mb-6">Supported Regulatory Authorities</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gradient-to-br from-purple-900/30 to-blue-900/30 rounded-xl p-6 border border-purple-500/40">
              <h3 className="text-xl font-semibold text-white mb-3">Building & Construction Authority (BCA)</h3>
              <p className="text-gray-300 leading-relaxed">
                Structural safety, accessibility, and building code compliance validation.
              </p>
            </div>
            <div className="bg-gradient-to-br from-purple-900/30 to-blue-900/30 rounded-xl p-6 border border-purple-500/40">
              <h3 className="text-xl font-semibold text-white mb-3">Singapore Civil Defence Force (SCDF)</h3>
              <p className="text-gray-300 leading-relaxed">
                Fire safety requirements, emergency exits, and evacuation route compliance.
              </p>
            </div>
            <div className="bg-gradient-to-br from-purple-900/30 to-blue-900/30 rounded-xl p-6 border border-purple-500/40">
              <h3 className="text-xl font-semibold text-white mb-3">Urban Redevelopment Authority (URA)</h3>
              <p className="text-gray-300 leading-relaxed">
                Land use, plot ratio, and urban planning regulation checks.
              </p>
            </div>
            <div className="bg-gradient-to-br from-purple-900/30 to-blue-900/30 rounded-xl p-6 border border-purple-500/40">
              <h3 className="text-xl font-semibold text-white mb-3">+ 4 Additional Agencies</h3>
              <p className="text-gray-300 leading-relaxed">
                Comprehensive coverage across environmental, utilities, transport, and heritage authorities.
              </p>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-white mb-6">Key Benefits</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-slate-800/50 rounded-xl p-6 border border-purple-500/20 text-center">
              <div className="text-4xl font-bold text-purple-400 mb-2">80%</div>
              <p className="text-gray-300">Faster Compliance Review</p>
            </div>
            <div className="bg-slate-800/50 rounded-xl p-6 border border-purple-500/20 text-center">
              <div className="text-4xl font-bold text-purple-400 mb-2">95%</div>
              <p className="text-gray-300">Violation Detection Accuracy</p>
            </div>
            <div className="bg-slate-800/50 rounded-xl p-6 border border-purple-500/20 text-center">
              <div className="text-4xl font-bold text-purple-400 mb-2">7</div>
              <p className="text-gray-300">Authorities Validated</p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default CaseStudy2;
